import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';

import { User, UserCreate, UserRole, UserUpdate } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import SortableHeader from '../components/SortableHeader';
import { useTableSort } from '../utils/tableSort';
import { useToast } from '../components/ToastProvider';
import { roleLabel } from '../utils/roleLabels';
import { apiErrorMessage } from '../utils/apiError';

const MIN_PASSWORD_LENGTH = 8;

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'KALOTTE', label: 'Kalotte - Vollzugriff' },
  { value: 'RUBY', label: 'Ruby - Eingeschränkter Zugriff' },
  { value: 'KALOTTE_VIEWER', label: 'Kalotte Viewer - Nur Lesezugriff' },
  { value: 'KALOTTE_OPERATOR', label: 'Kalotte Operator - Produktion' },
];

const EMPTY_CREATE: UserCreate = {
  username: '',
  email: '',
  full_name: '',
  role: 'RUBY',
  password: '',
};

interface EditForm {
  username: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
}

function roleColor(role: UserRole): 'primary' | 'info' | 'secondary' | 'default' {
  switch (role) {
    case 'KALOTTE':
      return 'primary';
    case 'KALOTTE_VIEWER':
      return 'info';
    case 'KALOTTE_OPERATOR':
      return 'default';
    default:
      return 'secondary';
  }
}

function RoleSelect({
  value,
  onChange,
  disabled,
  helperText,
}: {
  value: UserRole;
  onChange: (role: UserRole) => void;
  disabled?: boolean;
  helperText?: string;
}) {
  return (
    <FormControl fullWidth margin="dense" required disabled={disabled}>
      <InputLabel id="user-role-label">Rolle</InputLabel>
      <Select
        labelId="user-role-label"
        value={value}
        onChange={(e) => onChange(e.target.value as UserRole)}
        label="Rolle"
      >
        {ROLE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { toastError, toastSuccess } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<UserCreate>(EMPTY_CREATE);

  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);

  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  const { sort, order, onSort, sortRows } = useTableSort<
    'username' | 'full_name' | 'email' | 'role' | 'is_active' | 'created_at'
  >('username', 'asc', ['created_at']);
  const sortedUsers = sortRows(users, {
    username: (item) => item.username,
    full_name: (item) => item.full_name,
    email: (item) => item.email,
    role: (item) => roleLabel(item.role),
    is_active: (item) => (item.is_active ? 1 : 0),
    created_at: (item) => item.created_at,
  });

  useEffect(() => {
    void loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await apiService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  // ---- create -------------------------------------------------------------
  const createValid =
    createForm.username.trim() !== '' &&
    createForm.full_name.trim() !== '' &&
    createForm.email.trim() !== '' &&
    createForm.password.length >= MIN_PASSWORD_LENGTH;

  const handleCreate = async () => {
    setSaving(true);
    try {
      const created = await apiService.createUser({
        ...createForm,
        username: createForm.username.trim(),
        email: createForm.email.trim(),
        full_name: createForm.full_name.trim(),
      });
      toastSuccess(`Benutzer ${created.username} angelegt.`);
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      await loadUsers();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Erstellen des Benutzers'));
    } finally {
      setSaving(false);
    }
  };

  // ---- edit ---------------------------------------------------------------
  const openEdit = (user: User) => {
    setEditUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
  };

  const editIsSelf = editUser !== null && editUser.id === currentUser?.id;

  const editChanges = (): UserUpdate => {
    if (!editUser || !editForm) return {};
    const diff: UserUpdate = {};
    const username = editForm.username.trim();
    const email = editForm.email.trim();
    const fullName = editForm.full_name.trim();
    if (username !== editUser.username) diff.username = username;
    if (email !== editUser.email) diff.email = email;
    if (fullName !== editUser.full_name) diff.full_name = fullName;
    if (editForm.role !== editUser.role) diff.role = editForm.role;
    if (editForm.is_active !== editUser.is_active) diff.is_active = editForm.is_active;
    return diff;
  };

  const editValid =
    editForm !== null &&
    editForm.username.trim() !== '' &&
    editForm.email.trim() !== '' &&
    editForm.full_name.trim() !== '' &&
    Object.keys(editChanges()).length > 0;

  const handleEditSave = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const updated = await apiService.updateUser(editUser.id, editChanges());
      toastSuccess(`Benutzer ${updated.username} gespeichert.`);
      setEditUser(null);
      setEditForm(null);
      await loadUsers();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Speichern des Benutzers'));
    } finally {
      setSaving(false);
    }
  };

  // ---- password -----------------------------------------------------------
  const handleResetPassword = async () => {
    if (!passwordUser) return;
    setSaving(true);
    try {
      await apiService.resetPassword(passwordUser.id, newPassword);
      toastSuccess(`Passwort für ${passwordUser.username} zurückgesetzt.`);
      setPasswordUser(null);
      setNewPassword('');
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Zurücksetzen des Passworts'));
    } finally {
      setSaving(false);
    }
  };

  // ---- delete -------------------------------------------------------------
  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    try {
      await apiService.deleteUser(deleteUser.id);
      toastSuccess(`Benutzer ${deleteUser.username} gelöscht.`);
      setDeleteUser(null);
      await loadUsers();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Löschen des Benutzers'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Benutzer werden geladen..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Benutzerverwaltung"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Neuer Benutzer
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeader column="username" label="Benutzername" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="full_name" label="Name" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="email" label="E-Mail" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="role" label="Rolle" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="is_active" label="Status" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="created_at" label="Erstellt am" sort={sort} order={order} onSort={onSort} />
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const isSelf = user.id === currentUser?.id;
                return (
                  <TableRow key={user.id} hover sx={{ opacity: user.is_active ? 1 : 0.55 }}>
                    <TableCell sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                      {user.username}
                      {isSelf && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          (ich)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={roleLabel(user.role)}
                        color={roleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Aktiv' : 'Inaktiv'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(user.created_at).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          aria-label={`Benutzer ${user.username} bearbeiten`}
                          onClick={() => openEdit(user)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Passwort zurücksetzen">
                        <IconButton
                          size="small"
                          aria-label={`Passwort für ${user.username} zurücksetzen`}
                          onClick={() => {
                            setPasswordUser(user);
                            setNewPassword('');
                          }}
                        >
                          <KeyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!isSelf && (
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Benutzer ${user.username} löschen`}
                            onClick={() => setDeleteUser(user)}
                          >
                            <DeleteOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create */}
      <Dialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            autoFocus
            label="Benutzername"
            value={createForm.username}
            onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            margin="dense"
            autoComplete="off"
          />
          <TextField
            required
            fullWidth
            label="Vollständiger Name"
            value={createForm.full_name}
            onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
            margin="dense"
          />
          <TextField
            required
            fullWidth
            type="email"
            label="E-Mail"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            margin="dense"
          />
          <RoleSelect
            value={createForm.role}
            onChange={(role) => setCreateForm({ ...createForm, role })}
          />
          <TextField
            required
            fullWidth
            type="password"
            label="Passwort"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            margin="dense"
            autoComplete="new-password"
            helperText={`Mindestens ${MIN_PASSWORD_LENGTH} Zeichen`}
            error={createForm.password !== '' && createForm.password.length < MIN_PASSWORD_LENGTH}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setCreateForm(EMPTY_CREATE);
            }}
            disabled={saving}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => void handleCreate()}
            variant="contained"
            disabled={!createValid || saving}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editUser !== null}
        onClose={() => !saving && setEditUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Benutzer bearbeiten: {editUser?.username}</DialogTitle>
        <DialogContent>
          {editForm && (
            <>
              <TextField
                required
                fullWidth
                label="Benutzername"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                margin="dense"
                helperText="Anmeldename — Änderung wirkt ab der nächsten Anmeldung"
              />
              <TextField
                required
                fullWidth
                label="Vollständiger Name"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                margin="dense"
              />
              <TextField
                required
                fullWidth
                type="email"
                label="E-Mail"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                margin="dense"
              />
              <RoleSelect
                value={editForm.role}
                onChange={(role) => setEditForm({ ...editForm, role })}
                disabled={editIsSelf}
                helperText={editIsSelf ? 'Die eigene Rolle kann nicht geändert werden' : undefined}
              />
              <Stack sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      disabled={editIsSelf}
                    />
                  }
                  label="Konto aktiv"
                />
                <Typography variant="caption" color="text.secondary">
                  {editIsSelf
                    ? 'Das eigene Konto kann nicht deaktiviert werden.'
                    : 'Inaktive Benutzer können sich nicht mehr anmelden; ihre Daten bleiben erhalten.'}
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void handleEditSave()}
            variant="contained"
            disabled={!editValid || saving}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password */}
      <Dialog
        open={passwordUser !== null}
        onClose={() => !saving && setPasswordUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Passwort zurücksetzen: {passwordUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            type="password"
            label="Neues Passwort"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="dense"
            autoFocus
            autoComplete="new-password"
            helperText={`Mindestens ${MIN_PASSWORD_LENGTH} Zeichen`}
            error={newPassword !== '' && newPassword.length < MIN_PASSWORD_LENGTH}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordUser(null)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void handleResetPassword()}
            variant="contained"
            disabled={newPassword.length < MIN_PASSWORD_LENGTH || saving}
          >
            Passwort zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog
        open={deleteUser !== null}
        onClose={() => !saving && setDeleteUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Benutzer löschen?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Benutzer <strong>{deleteUser?.username}</strong> ({deleteUser?.full_name}) wird
              unwiderruflich gelöscht.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hat der Benutzer bereits Zertifikate, Chargen oder Aufträge angelegt, ist Löschen
              nicht möglich — dann stattdessen unter „Bearbeiten“ das Konto deaktivieren.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void handleDelete()}
            variant="contained"
            color="error"
            disabled={saving}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
